import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'

type AdminUser = {
  id: string
  name: string
  email: string
  role: string
  disabled: boolean
  createdAt: string
  workspace: { id: string; name: string }
  skillCount: number
  publisherVerified: boolean
  verificationLabel: string | null
}

export default function AdminUsersPage() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () =>
      (await apiClient.get<{ data: AdminUser[]; meta: unknown }>('/api/v1/admin/users')).data,
  })

  const toggleMut = useMutation({
    mutationFn: async (u: { id: string; disabled: boolean }) => {
      await apiClient.patch(`/api/v1/admin/users/${u.id}`, { disabled: u.disabled })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })

  if (isLoading) return <p className="text-sm text-neutral-500">Loading…</p>

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Users</h1>
        <p className="text-sm text-neutral-500">{data?.data.length ?? 0} total</p>
      </div>
      <div className="rounded border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-neutral-500 border-b">
            <tr>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Workspace</th>
              <th className="px-3 py-2">Skills</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(data?.data ?? []).map((u) => (
              <tr key={u.id} className="border-b">
                <td className="px-3 py-2">{u.name}</td>
                <td className="px-3 py-2 text-neutral-600">{u.email}</td>
                <td className="px-3 py-2">{u.role}</td>
                <td className="px-3 py-2 text-neutral-600">{u.workspace.name}</td>
                <td className="px-3 py-2">{u.skillCount}</td>
                <td className="px-3 py-2">
                  <span
                    className={
                      u.disabled
                        ? 'text-red-700 text-xs'
                        : 'text-green-700 text-xs'
                    }
                  >
                    {u.disabled ? 'disabled' : 'active'}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() =>
                      toggleMut.mutate({ id: u.id, disabled: !u.disabled })
                    }
                    disabled={toggleMut.isPending}
                    className="text-xs border rounded px-2 py-1 hover:bg-neutral-50"
                  >
                    {u.disabled ? 'Enable' : 'Disable'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
